import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { SitesService } from 'src/app/sites/sites.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { ModalComponent } from 'src/app/shared/modal/modal.component';
import { SiteDashboardService } from '../../site-dashboard.service';

@Component({
	selector: 'app-site-panels',
	templateUrl: './site-panels.component.html',
})

export class SitePanelsComponent implements OnInit, AfterViewInit, OnDestroy {

	@ViewChild('newPanelIdModal') newPanelIdModal?: ModalComponent;
	@ViewChild('deletePanelModal') deletePanelModal?: ModalComponent;

	readonly allYearQuarter = 'All-Year';

	currentSite: any = {};
	siteSubscription: Subscription = new Subscription();
	getSitePanelsSubscription: Subscription = new Subscription();

	installationDate: Date = null;
	yearList: any[] = [];
	quarterList: string[] = [];
	selectedYear: number;
	selectedQuarter: string;

	panels: any[] = [];
	toDeletePanel: any = null;
	devicesMinInstallationDate: number = null;

	newPanelId = '';
	newPanelIdError = '';

	columnConfig: ColumnsConfig[] = [
		{ key: 'panel_id', name: this.translate.instant('site_panels.panel_id'), type: 'string' },
		{ key: 'consumed_energy', name: this.translate.instant('site_panels.panel_energy_consumption'), type: 'number' },
		{ key: 'serial_numbers', name: this.translate.instant('site_panels.devices_serial_number'), type: 'list', hasSort: false },
		{ key: 'mac_addresses', name: this.translate.instant('site_panels.devices_mac_addresses'), type: 'list', hasSort: false },
		{ key: 'delete', name: this.translate.instant('g.delete'), type: 'icon' },
	];

	tableData: TableData[] = [];
	tableConfig: TableConfig = {
		hasExport: true,
		hasPagination: true,
		pageSize: 50,
		fitScreen: true,
		draggable: true,
		tableId: 'site-panels',
		fileName: this.translate.instant('site_panels.site_panels'),
		csvFileHeaderInfo: [],
	};

	constructor(
		private translate: TranslateService,
		private sideMenuService: SideMenuService,
		private sitesService: SitesService,
		private siteDashboardService: SiteDashboardService,
		private commonService: CommonService,
		private notificationMessage: NotificationMessageService,
	) { }

	ngOnInit() {
		this.siteSubscription = this.sideMenuService.currentSite.subscribe((siteInfo: any) => {
			// the site keeps emitting, only rebuild the filters when the site itself changed
			if (!siteInfo || !siteInfo.id || siteInfo.id == this.currentSite.id)
				return;

			this.currentSite = siteInfo;
			this.devicesMinInstallationDate = null;
			this.installationDate = siteInfo.installation_date ? moment.utc(siteInfo.installation_date).startOf('day').toDate() : null;

			this.prepareFilters();
			this.getSitePanels();
		});
	}

	ngAfterViewInit() {
		this.deletePanelModal?.onClose.subscribe((confirmed) => {
			if (confirmed)
				this.deletePanel();

			this.toDeletePanel = null;
		});
	}

	prepareFilters(keepSelection = false) {
		const { yearList, currentYear } = this.siteDashboardService.prepareYearsList([], this.installationDate);
		this.yearList = yearList;

		// a site with no installation date still lists panels, starting from the oldest offered year
		if (!this.installationDate)
			this.installationDate = moment.utc({ year: yearList[yearList.length - 1].id }).startOf('year').toDate();

		if (!keepSelection || !this.yearList.some(year => year.id == this.selectedYear))
			this.selectedYear = currentYear;

		this.setQuarterList(keepSelection ? this.selectedQuarter : `Q${moment().quarter()}`);
	}

	/**
	 * The year filter has to reach back to the oldest meter on the site, which can be older than
	 * the site record itself, so it is only known once the panels come back.
	 */
	applyDevicesMinInstallationDate(minInstallationDate: number) {
		if (!minInstallationDate || minInstallationDate == this.devicesMinInstallationDate)
			return;

		this.devicesMinInstallationDate = minInstallationDate;
		this.installationDate = moment.unix(minInstallationDate).utc().startOf('day').toDate();

		this.prepareFilters(true);
	}

	/**
	 * Only the quarters the site could have data in are offered: nothing after the running
	 * quarter, and nothing before the site was installed.
	 */
	getListQuarters() {
		const currentYear = moment().year();
		const installationYear = moment(this.installationDate).year();

		if (this.selectedYear != currentYear && this.selectedYear != installationYear)
			return [this.allYearQuarter, 'Q1', 'Q2', 'Q3', 'Q4'];

		const quarters = [this.allYearQuarter];
		for (let quarter = 1; quarter <= 4; quarter++) {
			const isAfterCurrentQuarter = this.selectedYear == currentYear && quarter > moment().quarter();
			const isBeforeInstallation = this.selectedYear == installationYear && quarter < moment(this.installationDate).quarter();

			if (isAfterCurrentQuarter || isBeforeInstallation)
				continue;

			quarters.push(`Q${quarter}`);
		}

		return quarters;
	}

	setQuarterList(selectedQuarter?: string) {
		this.quarterList = this.getListQuarters();

		this.selectedQuarter = selectedQuarter && this.quarterList.includes(selectedQuarter)
			? selectedQuarter
			: this.allYearQuarter;
	}

	selectYear() {
		this.setQuarterList();
		this.getSitePanels();
	}

	getSitePanels() {
		const request = this.sitesService.getSitePanels(this.currentSite.id, this.selectedYear, this.selectedQuarter);

		this.getSitePanelsSubscription = request.subscribe((res: any) => {
			this.panels = res?.panels || [];
			this.applyDevicesMinInstallationDate(res?.min_installation_date);
			this.prepareTableData();
		});
	}

	prepareTableData() {
		this.tableData = this.panels.map(panel => {
			const hasDevices = !!panel.mac_addresses.length;

			return {
				panel_id: { value: panel.panel_id },
				consumed_energy: { value: panel.consumed_energy },
				serial_numbers: { value: panel.serial_numbers.map(serialNumber => ({ text: serialNumber })) },
				mac_addresses: { value: panel.mac_addresses.map(macAddress => ({ text: macAddress })) },
				delete: {
					icon: 'trash',
					iconColor: hasDevices ? 'lightgrey' : 'red',
					action: () => this.confirmDeletePanel(panel),
				},
			};
		});

		this.tableConfig.csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
			customerName: this.currentSite.customer_name,
			siteName: this.currentSite.name,
			includeSpacing: false
		});
	}

	confirmDeletePanel(panel: any) {
		// a panel that still holds devices cannot be deleted
		if (panel.mac_addresses.length)
			return this.notificationMessage.setMessage('translate|site_panels.cannot_delete_panel', { clearOnXTimeNavigate: 1 });

		this.toDeletePanel = panel;
		this.deletePanelModal?.show();
	}

	deletePanel() {
		// the modal clears toDeletePanel as soon as it closes, so keep the id for the response
		const panelId = this.toDeletePanel.panel_id;

		this.sitesService.deletePanel(this.currentSite.id, panelId).subscribe(() => {
			this.panels = this.panels.filter(panel => panel.panel_id != panelId);
			this.prepareTableData();

			this.notificationMessage.setMessage('globalSuccessMsg');
		});
	}

	panelIdValidation(panelId: string) {
		if (!panelId) {
			this.newPanelIdError = this.translate.instant('g.field_is_required');
			return false;
		}

		const isExisting = this.panels.some(panel => panel.panel_id.trim().toLowerCase() == panelId.toLowerCase());
		if (isExisting) {
			this.newPanelIdError = this.translate.instant('settings.panel_id_is_exists');
			return false;
		}

		return true;
	}

	addNewPanelId() {
		const panelId = (this.newPanelId || '').trim();
		if (!this.panelIdValidation(panelId))
			return;

		this.sitesService.insertPanel(this.currentSite.id, null, panelId).subscribe(() => {
			this.panels = [...this.panels, { panel_id: panelId, mac_addresses: [], serial_numbers: [], consumed_energy: 0 }];
			this.prepareTableData();

			this.newPanelId = '';
			this.newPanelIdModal?.hide();
			this.notificationMessage.setMessage('globalSuccessMsg');
		});
	}

	openNewPanelIdModal() {
		this.newPanelId = '';
		this.newPanelIdError = '';
		this.newPanelIdModal?.show();
	}

	ngOnDestroy() {
		this.siteSubscription?.unsubscribe();
		this.getSitePanelsSubscription?.unsubscribe();
	}
}
